import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTestMonitoring } from './useTestMonitoring';
import { getCameraDiagnostics, logCameraDiagnostics, testCameraAccess } from '@/utils/camera-diagnostics';

interface UseWebcamMonitoringProps {
  testResponseId: string;
  currentQuestionId?: string;
  isActive: boolean;
}

interface WebcamState {
  stream: MediaStream | null;
  isCapturing: boolean;
  hasPermission: boolean | null;
  error: string | null;
  isInitialized: boolean;
  isBackgroundRecording: boolean;
  monitoringMethod: 'live' | 'background';
}

export const useWebcamMonitoring = ({ 
  testResponseId, 
  currentQuestionId, 
  isActive 
}: UseWebcamMonitoringProps) => {
  const [state, setState] = useState<WebcamState>({
    stream: null,
    isCapturing: false,
    hasPermission: null,
    error: null,
    isInitialized: false,
    isBackgroundRecording: false,
    monitoringMethod: 'live'
  });
  
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const monitoringRecorderRef = useRef<MediaRecorder | null>(null);
  const isProcessingChunkRef = useRef<boolean>(false);
  // CRITICAL FIX: Track initialization in progress to prevent cleanup effect from firing
  const isInitializingRef = useRef<boolean>(false);
  
  const { logEvent } = useTestMonitoring(testResponseId, currentQuestionId);

  console.log('[useWebcamMonitoring] Hook called with:', { 
    testResponseId, 
    currentQuestionId, 
    isActive,
    currentState: state,
    timestamp: new Date().toISOString()
  });

  // Initialize camera with enhanced error handling and retry logic
  const initializeCamera = useCallback(async () => {
    if (state.isInitialized || isInitializingRef.current) {
      console.log('[WebcamMonitoring] Camera already initialized or initializing, skipping');
      return;
    }
    
    // CRITICAL: Mark as initializing to prevent cleanup effect from firing
    isInitializingRef.current = true;
    console.log('[WebcamMonitoring] Starting camera initialization process...');
    
    // Get comprehensive diagnostics
    const diagnostics = await getCameraDiagnostics();
    logCameraDiagnostics('Camera initialization start', diagnostics);
    
    await logEvent({
      event_type: 'webcam_capture',
      event_data: { 
        action: 'initialization_start',
        diagnostics 
      },
      severity: 1,
      created_at: new Date().toISOString()
    });
    
    // Check browser support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errorMessage = 'Camera not supported in this browser';
      console.error('[WebcamMonitoring] Browser compatibility error:', errorMessage);
      
      setState(prev => ({
        ...prev,
        error: errorMessage,
        hasPermission: false,
        isInitialized: true
      }));
      
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { action: 'browser_not_supported', userAgent: navigator.userAgent },
        severity: 3,
        created_at: new Date().toISOString()
      });
      return;
    }
    
    // Check current permission status if supported
    try {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
        console.log('[WebcamMonitoring] Current camera permission:', permission.state);
        
        await logEvent({
          event_type: 'webcam_capture',
          event_data: { action: 'permission_check', permissionState: permission.state },
          severity: 1,
          created_at: new Date().toISOString()
        });
      }
    } catch (permErr) {
      console.log('[WebcamMonitoring] Permission query not supported or failed:', permErr);
    }
    
    // Attempt camera access with retry logic
    let attempt = 0;
    const maxAttempts = 3;
    
    while (attempt < maxAttempts) {
      attempt++;
      console.log(`[WebcamMonitoring] Camera access attempt ${attempt}/${maxAttempts}`);
      
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          },
          audio: false
        });
        
        console.log('[WebcamMonitoring] Camera access granted, stream created:', {
          streamId: mediaStream.id,
          tracks: mediaStream.getVideoTracks().length,
          trackSettings: mediaStream.getVideoTracks()[0]?.getSettings()
        });
        
        // Validate stream has active tracks
        const videoTracks = mediaStream.getVideoTracks();
        if (videoTracks.length === 0 || videoTracks[0].readyState !== 'live') {
          throw new Error('Camera stream is not active');
        }
        
        // Store stream reference
        streamRef.current = mediaStream;
        
        // Create canvas for capturing
        if (!canvasRef.current) {
          const canvas = document.createElement('canvas');
          canvas.width = 640;
          canvas.height = 480;
          canvasRef.current = canvas;
          console.log('[WebcamMonitoring] Canvas created for capturing');
        }
        
        setState(prev => ({
          ...prev,
          stream: mediaStream,
          hasPermission: true,
          error: null,
          isInitialized: true
        }));
        
        await logEvent({
          event_type: 'webcam_capture',
          event_data: { 
            action: 'camera_initialized', 
            streamId: mediaStream.id,
            attempt,
            tracks: videoTracks.length,
            trackSettings: videoTracks[0]?.getSettings()
          },
          severity: 1,
          created_at: new Date().toISOString()
        });
        
        console.log('[WebcamMonitoring] Camera initialized successfully on attempt', attempt);
        isInitializingRef.current = false;
        return; // Success, exit retry loop
        
      } catch (err) {
        console.error(`[WebcamMonitoring] Camera initialization error on attempt ${attempt}:`, err);
        
        // On final attempt, set error state
        if (attempt === maxAttempts) {
          const errorMessage = err instanceof Error ? err.message : 'Camera access denied';
          
          setState(prev => ({
            ...prev,
            error: errorMessage,
            hasPermission: false,
            isInitialized: true
          }));
          
          await logEvent({
            event_type: 'webcam_capture',
            event_data: { 
              action: 'camera_error', 
              error: errorMessage,
              attempt,
              totalAttempts: maxAttempts 
            },
            severity: 3,
            created_at: new Date().toISOString()
          });
          isInitializingRef.current = false;
        } else {
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
  }, [state.isInitialized, logEvent]);

  // Upload snapshot blob (shared by both live and background capture)
  const uploadSnapshotBlob = useCallback(async (blob: Blob) => {
    try {
      // Generate filename
      const timestamp = new Date().toISOString();
      const filename = `${testResponseId}/${timestamp}.jpg`;
      
      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('webcam-snapshots')
        .upload(filename, blob, {
          contentType: 'image/jpeg',
          cacheControl: '3600'
        });
      
      if (uploadError) {
        console.error('[WebcamMonitoring] Upload error:', uploadError);
        return;
      }
      
      // Save to database with file path
      const { error: dbError } = await supabase
        .from('test_webcam_snapshots')
        .insert({
          test_response_id: testResponseId,
          image_url: filename,
          question_id: currentQuestionId,
          captured_at: timestamp
        });
      
      if (dbError) {
        console.error('[WebcamMonitoring] Database error:', dbError);
        return;
      }
      
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { 
          action: 'snapshot_captured',
          filename,
          question_id: currentQuestionId,
          method: state.monitoringMethod
        },
        severity: 1,
        created_at: timestamp
      });
      
    } catch (err) {
      console.error('[WebcamMonitoring] Upload error:', err);
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { 
          action: 'upload_error', 
          error: err instanceof Error ? err.message : 'Unknown error' 
        },
        severity: 2,
        created_at: new Date().toISOString()
      });
    }
  }, [testResponseId, currentQuestionId, logEvent, state.monitoringMethod]);

  // Handle MediaRecorder chunks for background monitoring
  const handleRecordingChunk = useCallback(async (event: BlobEvent) => {
    if (!event.data.size || isProcessingChunkRef.current) return;
    
    isProcessingChunkRef.current = true;
    
    try {
      // Create video element to extract frame from chunk
      const video = document.createElement('video');
      video.src = URL.createObjectURL(event.data);
      video.muted = true;
      
      await new Promise((resolve, reject) => {
        video.onloadeddata = resolve;
        video.onerror = reject;
        video.load();
      });
      
      // Wait for first frame to be available
      await new Promise(resolve => {
        video.currentTime = 0;
        video.onseeked = resolve;
      });
      
      // Extract frame using canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 640;
      canvas.height = 480;
      
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert to blob and upload
        canvas.toBlob(async (blob) => {
          if (blob) {
            await uploadSnapshotBlob(blob);
          }
        }, 'image/jpeg', 0.8);
      }
      
      // Cleanup
      URL.revokeObjectURL(video.src);
      
    } catch (error) {
      console.error('[WebcamMonitoring] Error processing recording chunk:', error);
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { 
          action: 'background_chunk_error', 
          error: error instanceof Error ? error.message : 'Unknown error' 
        },
        severity: 2,
        created_at: new Date().toISOString()
      });
    } finally {
      isProcessingChunkRef.current = false;
    }
  }, [logEvent, uploadSnapshotBlob]);

  // Start background MediaRecorder
  const startBackgroundRecording = useCallback(async () => {
    if (!streamRef.current || monitoringRecorderRef.current) return;
    
    try {
      const recorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp8',
      });
      
      recorder.ondataavailable = handleRecordingChunk;
      recorder.onerror = (event) => {
        console.error('[WebcamMonitoring] MediaRecorder error:', event);
      };
      
      recorder.start(10000); // 10-second chunks (EMERGENCY FIX for DB connection pool)
      monitoringRecorderRef.current = recorder;
      
      setState(prev => ({ ...prev, isBackgroundRecording: true }));
      
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { action: 'background_recording_started' },
        severity: 1,
        created_at: new Date().toISOString()
      });
      
      console.log('[WebcamMonitoring] Background recording started');
      
    } catch (error) {
      console.error('[WebcamMonitoring] Failed to start background recording:', error);
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { 
          action: 'background_recording_error', 
          error: error instanceof Error ? error.message : 'Unknown error' 
        },
        severity: 3,
        created_at: new Date().toISOString()
      });
    }
  }, [handleRecordingChunk, logEvent]);

  // Stop background MediaRecorder
  const stopBackgroundRecording = useCallback(async () => {
    if (monitoringRecorderRef.current) {
      try {
        const recorder = monitoringRecorderRef.current;
        console.log('[WebcamMonitoring] Stopping MediaRecorder, state:', recorder.state);
        
        // Only stop if recording or paused
        if (recorder.state === 'recording' || recorder.state === 'paused') {
          recorder.stop();
          
          // Wait for the recorder to actually stop
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
              console.warn('[WebcamMonitoring] MediaRecorder stop timeout');
              resolve();
            }, 2000);
            
            recorder.onstop = () => {
              clearTimeout(timeout);
              console.log('[WebcamMonitoring] MediaRecorder stopped successfully');
              resolve();
            };
          });
        }
        
        monitoringRecorderRef.current = null;
        setState(prev => ({ ...prev, isBackgroundRecording: false }));
        
        await logEvent({
          event_type: 'webcam_capture',
          event_data: { action: 'background_recording_stopped' },
          severity: 1,
          created_at: new Date().toISOString()
        });
        
        console.log('[WebcamMonitoring] Background recording stopped');
      } catch (error) {
        console.error('[WebcamMonitoring] Error stopping background recording:', error);
        // Ensure cleanup even if error occurs
        monitoringRecorderRef.current = null;
        setState(prev => ({ ...prev, isBackgroundRecording: false }));
      }
    }
  }, [logEvent]);

  // Capture snapshot
  const captureSnapshot = useCallback(async () => {
    const stream = streamRef.current;
    if (!canvasRef.current || !stream) {
      console.log('[WebcamMonitoring] Cannot capture - missing dependencies');
      return;
    }

    // Create a video element to capture from the stream
    let videoElement = document.querySelector('video[data-webcam-preview]') as HTMLVideoElement;
    
    // If video element doesn't exist or isn't ready, create a temporary one
    if (!videoElement || videoElement.readyState < 2) {
      console.log('[WebcamMonitoring] Creating temporary video element for capture');
      videoElement = document.createElement('video');
      videoElement.srcObject = stream;
      videoElement.autoplay = true;
      videoElement.muted = true;
      
      // Wait for video to be ready
      await new Promise((resolve) => {
        videoElement.addEventListener('loadedmetadata', resolve, { once: true });
      });
      
      // Additional wait to ensure video is displaying
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;
      
      // Draw current video frame to canvas
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      
      // Convert to blob and upload using shared function
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/jpeg', 0.8);
      });
      
      await uploadSnapshotBlob(blob);
      
    } catch (err) {
      console.error('[WebcamMonitoring] Capture error:', err);
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { 
          action: 'capture_error', 
          error: err instanceof Error ? err.message : 'Unknown error' 
        },
        severity: 2,
        created_at: new Date().toISOString()
      });
    }
  }, [uploadSnapshotBlob]);

  // Start capturing
  const startCapture = useCallback(async () => {
    if (!streamRef.current) {
      console.log('[WebcamMonitoring] Cannot start capture - no stream available');
      return;
    }
    
    if (state.isCapturing) {
      console.log('[WebcamMonitoring] Already capturing, skipping start');
      return;
    }
    
    console.log('[WebcamMonitoring] Starting capture...');
    setState(prev => ({ ...prev, isCapturing: true }));
    
    // Start background MediaRecorder for continuous monitoring
    await startBackgroundRecording();
    
    // Also start live capture for immediate feedback (when window is visible)
    if (!document.hidden) {
      setState(prev => ({ ...prev, monitoringMethod: 'live' }));
      
      // Capture immediately
      await captureSnapshot();
      
      // Then capture every 10 seconds (EMERGENCY FIX for DB connection pool)
      intervalRef.current = setInterval(() => {
        if (!document.hidden) {
          captureSnapshot();
        }
      }, 10000);
    } else {
      setState(prev => ({ ...prev, monitoringMethod: 'background' }));
    }
    
    await logEvent({
      event_type: 'webcam_capture',
      event_data: { action: 'capture_started', method: state.monitoringMethod },
      severity: 1,
      created_at: new Date().toISOString()
    });
    
    console.log('[WebcamMonitoring] Started capturing with hybrid approach');
  }, [state.isCapturing, captureSnapshot, logEvent, startBackgroundRecording]);

  // Stop capturing
  const stopCapture = useCallback(async () => {
    // Stop live capture interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Stop background recording
    await stopBackgroundRecording();
    
    setState(prev => ({ ...prev, isCapturing: false }));
    
    await logEvent({
      event_type: 'webcam_capture',
      event_data: { action: 'capture_stopped' },
      severity: 1,
      created_at: new Date().toISOString()
    });
    
    console.log('[WebcamMonitoring] Stopped capturing');
  }, [logEvent, stopBackgroundRecording]);

  // Complete cleanup
  const cleanup = useCallback(async () => {
    console.log('[WebcamMonitoring] Starting cleanup...');
    
    try {
      // Stop capturing
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log('[WebcamMonitoring] Cleared capture interval');
      }
      
      // Stop background recording with timeout protection
      console.log('[WebcamMonitoring] Stopping background recording...');
      await Promise.race([
        stopBackgroundRecording(),
        new Promise<void>((resolve) => {
          setTimeout(() => {
            console.warn('[WebcamMonitoring] Background recording stop timeout');
            resolve();
          }, 3000);
        })
      ]);
      
      // Stop all tracks with verification
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        console.log(`[WebcamMonitoring] Stopping ${tracks.length} tracks...`);
        
        for (const track of tracks) {
          try {
            track.stop();
            console.log(`[WebcamMonitoring] Stopped ${track.kind} track, readyState:`, track.readyState);
          } catch (trackError) {
            console.error('[WebcamMonitoring] Error stopping track:', trackError);
          }
        }
        
        // Verify all tracks are stopped
        const activeTracks = tracks.filter(track => track.readyState === 'live');
        if (activeTracks.length > 0) {
          console.warn(`[WebcamMonitoring] Warning: ${activeTracks.length} tracks still active after cleanup`);
        }
        
        streamRef.current = null;
      }
      
      // Reset state
      setState({
        stream: null,
        isCapturing: false,
        hasPermission: null,
        error: null,
        isInitialized: false,
        isBackgroundRecording: false,
        monitoringMethod: 'live'
      });
      
      await logEvent({
        event_type: 'webcam_capture',
        event_data: { action: 'cleanup_completed' },
        severity: 1,
        created_at: new Date().toISOString()
      });
      
      console.log('[WebcamMonitoring] Cleanup completed successfully');
      
    } catch (error) {
      console.error('[WebcamMonitoring] Error during cleanup:', error);
      
      // Force cleanup even if errors occur
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (monitoringRecorderRef.current) {
        monitoringRecorderRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          try { track.stop(); } catch {}
        });
        streamRef.current = null;
      }
      
      setState({
        stream: null,
        isCapturing: false,
        hasPermission: null,
        error: null,
        isInitialized: false,
        isBackgroundRecording: false,
        monitoringMethod: 'live'
      });
      
      // Still log completion even with errors
      try {
        await logEvent({
          event_type: 'webcam_capture',
          event_data: { 
            action: 'cleanup_completed_with_errors',
            error: error instanceof Error ? error.message : 'Unknown error'
          },
          severity: 2,
          created_at: new Date().toISOString()
        });
      } catch (logError) {
        console.error('[WebcamMonitoring] Failed to log cleanup completion:', logError);
      }
    }
  }, [logEvent, stopBackgroundRecording]);

  // Handle page visibility changes for hybrid monitoring
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!state.isCapturing) return;
      
      if (document.hidden) {
        // Switch to background-only monitoring
        setState(prev => ({ ...prev, monitoringMethod: 'background' }));
        
        // Stop live capture interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        
        logEvent({
          event_type: 'webcam_capture',
          event_data: { action: 'switched_to_background' },
          severity: 2,
          created_at: new Date().toISOString()
        });
        
        console.log('[WebcamMonitoring] Window hidden, switched to background monitoring');
        
      } else {
        // Switch back to live monitoring
        setState(prev => ({ ...prev, monitoringMethod: 'live' }));
        
        // Resume live capture
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            if (!document.hidden) {
              captureSnapshot();
            }
          }, 10000); // EMERGENCY FIX for DB connection pool
        }
        
        logEvent({
          event_type: 'webcam_capture',
          event_data: { action: 'switched_to_live' },
          severity: 1,
          created_at: new Date().toISOString()
        });
        
        console.log('[WebcamMonitoring] Window visible, switched to live monitoring');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.isCapturing, captureSnapshot, logEvent]);

  // Initialize when active
  useEffect(() => {
    console.log('[WebcamMonitoring] Effect: Initialize when active', { 
      isActive, 
      isInitialized: state.isInitialized 
    });
    
    if (isActive && !state.isInitialized) {
      console.log('[WebcamMonitoring] Triggering camera initialization');
      initializeCamera();
    }
  }, [isActive, state.isInitialized, initializeCamera]);

  // Start/stop capturing based on active state
  useEffect(() => {
    console.log('[WebcamMonitoring] Effect: Start/stop capturing', { 
      isActive, 
      hasPermission: state.hasPermission, 
      hasStream: !!state.stream, 
      isCapturing: state.isCapturing 
    });
    
    if (isActive && state.hasPermission && state.stream && !state.isCapturing) {
      console.log('[WebcamMonitoring] Conditions met, starting capture');
      startCapture();
    } else if (!isActive && state.isCapturing) {
      console.log('[WebcamMonitoring] Not active, stopping capture');
      stopCapture();
    }
  }, [isActive, state.hasPermission, state.stream, state.isCapturing, startCapture, stopCapture]);

  // Cleanup when inactive
  useEffect(() => {
    // CRITICAL FIX: Don't cleanup during initialization - this was causing the race condition
    // where cleanup fired immediately after camera init because isActive was set after init started
    if (isInitializingRef.current) {
      console.log('[WebcamMonitoring] Effect: Skipping cleanup - initialization in progress');
      return;
    }
    
    console.log('[WebcamMonitoring] Effect: Cleanup when inactive', { 
      isActive, 
      hasStream: !!state.stream, 
      isCapturing: state.isCapturing, 
      isInitialized: state.isInitialized 
    });
    
    if (!isActive && (state.stream || state.isCapturing || state.isInitialized)) {
      console.log('[WebcamMonitoring] Triggering cleanup due to inactive state');
      cleanup();
    }
  }, [isActive, state.stream, state.isCapturing, state.isInitialized, cleanup]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    stream: state.stream,
    isCapturing: state.isCapturing,
    hasPermission: state.hasPermission,
    error: state.error,
    isInitialized: state.isInitialized,
    isBackgroundRecording: state.isBackgroundRecording,
    monitoringMethod: state.monitoringMethod,
    initializeCamera,
    startCapture,
    stopCapture,
    cleanup,
    videoRef
  };
};